import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { supabase } from '@/supabaseClient';
import { api } from '../api';
import * as Sentry from '@sentry/browser';

export function RequestForm({ onRequestCreated, onCancel, onAuthRequired, prefilledDescription, fromDiscovery }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: prefilledDescription || ''
  });

  // Update description if prefilledDescription changes
  useEffect(() => {
    if (prefilledDescription && prefilledDescription !== formData.description) {
      setFormData(prev => ({
        ...prev,
        description: prefilledDescription
      }));
    }
  }, [prefilledDescription, formData.description]);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingName, setIsGeneratingName] = useState(false);
  const [isCheckingSimilarity, setIsCheckingSimilarity] = useState(false);
  const [similarityResults, setSimilarityResults] = useState(null);
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting || isCheckingSimilarity) return;
    
    if (!user) {
      // Trigger auth modal instead of blocking silently
      onAuthRequired?.();
      return;
    }

    // First, check for similar requests
    await checkSimilarRequests();
  };

  const checkSimilarRequests = async () => {
    if (!formData.title.trim() || !formData.description.trim()) return;

    setIsCheckingSimilarity(true);
    setErrors({});
    setSimilarityResults(null);

    try {
      console.log('Checking for similar requests...');
      
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch('/api/check-similar-requests', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description
        })
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.warn('Authentication error in similarity check, user needs to re-authenticate');
          setErrors({ form: 'Authentication expired. Please refresh the page and try again.' });
          return;
        }
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to check for similar requests');
      }

      const data = await response.json();
      console.log('Similarity check completed:', data);
      
      setSimilarityResults(data);
      
      // If no similar requests found, proceed with submission
      if (!data.hasSimilarRequests) {
        await proceedWithSubmission();
      }
      
    } catch (error) {
      console.error('Error checking for similar requests:', error);
      Sentry.captureException(error, {
        extra: { 
          title: formData.title,
          description: formData.description?.substring(0, 100) + '...',
          errorMessage: error.message 
        }
      });
      
      // If similarity check fails, ask user if they want to proceed anyway
      setErrors({ 
        form: 'Unable to check for similar requests. You can still submit your request, but there might be similar ones already.' 
      });
    } finally {
      setIsCheckingSimilarity(false);
    }
  };

  const proceedWithSubmission = async () => {
    setIsSubmitting(true);
    setErrors({});

    try {
      console.log('Proceeding with request submission...');
      const newRequest = await api.createRequest(formData);
      
      // Track request submission
      const { trackSubmitRequest } = await import('/src/services/analytics/index.js');
      trackSubmitRequest(newRequest.id, formData.title);
      
      onRequestCreated(newRequest);
      setFormData({ title: '', description: '' });
    } catch (error) {
      console.error('Error creating request:', error);
      Sentry.captureException(error);
      
      // Handle profile setup required error
      if (error.requiresProfileSetup || error.message.includes('User profile not found') || error.message.includes('profile setup')) {
        setErrors({ 
          form: 'Your profile setup is incomplete. Please refresh the page to complete your profile setup, then try again.' 
        });
      } else if (error.message.includes('validation')) {
        setErrors({ form: 'Please check your input and try again.' });
      } else {
        setErrors({ form: 'Failed to create request. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitAnyway = async () => {
    setSimilarityResults(null);
    await proceedWithSubmission();
  };

  const handleCancelSubmission = () => {
    setSimilarityResults(null);
    setIsSubmitting(false);
    setIsCheckingSimilarity(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleGenerateAppName = async () => {
    if (!formData.description.trim() || isGeneratingName) return;
    
    if (!user) {
      // Trigger auth modal for AI features
      onAuthRequired?.();
      return;
    }

    setIsGeneratingName(true);
    setErrors({});

    try {
      console.log('Generating app name for description:', formData.description.substring(0, 100) + '...');
      
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch('/api/generate-app-name', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ description: formData.description }),
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.warn('Authentication error in app name generation, user needs to re-authenticate');
          setErrors({ generateName: 'Authentication expired. Please refresh the page and try again.' });
          return;
        }
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate app name');
      }

      const data = await response.json();
      console.log('Generated app name received:', data.generatedName);
      
      setFormData(prev => ({
        ...prev,
        title: data.generatedName
      }));
    } catch (error) {
      console.error('Error generating app name:', error);
      Sentry.captureException(error, {
        extra: { 
          description: formData.description,
          errorMessage: error.message 
        }
      });
      
      setErrors({ generateName: 'Failed to generate app name. Please try again.' });
    } finally {
      setIsGeneratingName(false);
    }
  };

  const titleCharCount = formData.title.length;
  const descCharCount = formData.description.length;
  const isFormValid = formData.title.trim() && formData.description.trim() && titleCharCount <= 200 && descCharCount <= 2000;
  const isProcessing = isSubmitting || isCheckingSimilarity;

  return (
    <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      {/* Form Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-4 py-4 sm:px-8 sm:py-6">
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
          {fromDiscovery ? 'Request This App' : 'Submit Your App Idea'}
        </h3>
        <p className="text-blue-100 text-sm sm:text-base">
          {fromDiscovery 
            ? 'Since no good existing solution was found, let\'s see if others want this app too!'
            : 'Share your vision and let the community decide if it should be built'
          }
        </p>
        {!similarityResults && (
          <p className="text-blue-200 text-xs sm:text-sm mt-2">
            💡 We'll automatically check if similar requests already exist before submitting
          </p>
        )}
      </div>
      
      <form onSubmit={handleSubmit} className="p-4 sm:p-8 space-y-6">
        {/* Description Field - Now First */}
        <div>
          <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
            App Description *
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Describe your app idea in detail:
• What problem does it solve?
• Who would use it?
• What are the key features?
• Why would people pay for it?

Be specific and inspiring!"
            rows={6}
            className="w-full px-3 py-3 sm:px-4 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent box-border text-gray-900 shadow-sm hover:shadow-md transition-shadow resize-none text-sm sm:text-base"
            maxLength={2000}
            required
          />
          <div className="flex justify-between items-center mt-2">
            <div className="text-xs text-gray-500">
              The more detailed, the better the response
            </div>
            <div className={`text-xs ${descCharCount > 1800 ? 'text-red-500' : 'text-gray-500'}`}>
              {descCharCount}/2000 characters
            </div>
          </div>
        </div>

        {/* Title Field - Now Second with AI Generation */}
        <div>
          <label htmlFor="title" className="block text-sm font-semibold text-gray-700 mb-2">
            App Title *
          </label>
          <div className="space-y-3">
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., AI-Powered Fitness Tracker, Smart Home Manager"
              className="w-full px-3 py-3 sm:px-4 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent box-border text-gray-900 text-base sm:text-lg shadow-sm hover:shadow-md transition-shadow"
              maxLength={200}
              required
            />
            
            {/* AI Generate Button */}
            <button
              type="button"
              onClick={handleGenerateAppName}
              disabled={!formData.description.trim() || isGeneratingName}
              className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium cursor-pointer transition-all duration-200 active:scale-95 disabled:transform-none flex items-center justify-center text-sm sm:text-base"
            >
              {isGeneratingName ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Generating Name...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  {user ? 'Generate App Name with AI' : 'Sign In for AI Features'}
                </>
              )}
            </button>
            
            {/* Error for name generation */}
            {errors.generateName && (
              <div className="text-red-600 text-sm">
                {errors.generateName}
              </div>
            )}
          </div>
          
          <div className="flex justify-between items-center mt-2">
            <div className="text-xs text-gray-500">
              Keep it clear and descriptive
            </div>
            <div className={`text-xs ${titleCharCount > 180 ? 'text-red-500' : 'text-gray-500'}`}>
              {titleCharCount}/200 characters
            </div>
          </div>
        </div>

        {/* Similarity Results Display */}
        {similarityResults && similarityResults.hasSimilarRequests && (
          <div className="bg-amber-50 border-2 border-amber-200 rounded-lg sm:rounded-xl p-6">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 bg-amber-500 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.865-.833-2.635 0L4.168 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-amber-800 mb-2">Similar Requests Found</h3>
                <p className="text-amber-700 mb-4">{similarityResults.summary}</p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              {similarityResults.similarRequests.map((request, index) => (
                <div 
                  key={request.id} 
                  onClick={() => navigate(`/request/${request.id}`)}
                  className="bg-white rounded-lg border border-amber-200 p-4 hover:border-amber-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                >
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-semibold text-gray-900 text-sm group-hover:text-blue-600 transition-colors">
                      {request.title}
                      <svg className="inline w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </h4>
                    <div className="flex items-center space-x-3 text-xs text-gray-600 flex-shrink-0">
                      <span className="flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                        </svg>
                        {request.votes}
                      </span>
                      <span className="flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        {request.comments}
                      </span>
                      <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-medium">
                        {request.similarity_score}/10 similar
                      </span>
                    </div>
                  </div>
                  
                  {/* Request Description */}
                  <p className="text-gray-700 text-sm mb-3 line-clamp-3">
                    {request.description || 'No description available'}
                  </p>
                  
                  {/* Similarity Reasoning */}
                  <div className="bg-amber-50 rounded-lg p-3 border-l-3 border-amber-300">
                    <p className="text-amber-800 text-xs font-medium mb-1">Why it's similar:</p>
                    <p className="text-amber-700 text-xs">{request.reasoning}</p>
                  </div>
                  
                  {/* Author and Date */}
                  {(request.author || request.created_date) && (
                    <div className="flex items-center text-xs text-gray-500 mt-3 pt-2 border-t border-gray-100">
                      {request.author && (
                        <span className="flex items-center mr-4">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          {request.author}
                        </span>
                      )}
                      {request.created_date && (
                        <span className="flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {request.created_date}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* Click hint */}
                  <div className="text-xs text-blue-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    👆 Click to view full request, vote, and comment
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-amber-100 rounded-lg p-4 mb-4">
              <p className="text-amber-800 text-sm font-medium mb-2">
                🤔 Still want to submit your request?
              </p>
              <p className="text-amber-700 text-sm">
                Even though similar requests exist, yours might have a unique angle or improvements that make it worth submitting. Consider explaining what's different about your vision.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleSubmitAnyway}
                disabled={isProcessing}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all duration-200 flex items-center justify-center text-sm"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    {user ? 'Submit Anyway' : 'Sign In to Submit'}
                  </>
                )}
              </button>
              <button
                onClick={handleCancelSubmission}
                disabled={isProcessing}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 font-semibold rounded-lg transition-colors text-sm disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* No Similar Requests - Auto Submit Message */}
        {similarityResults && !similarityResults.hasSimilarRequests && (
          <div className="bg-green-50 border-2 border-green-200 rounded-lg sm:rounded-xl p-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-green-800 font-medium">No similar requests found!</p>
                <p className="text-green-700 text-sm">Your idea appears to be unique. Submitting your request...</p>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {errors.form && (
          <div className="bg-red-50 border border-red-200 rounded-lg sm:rounded-xl p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-red-800">Error submitting request</p>
                <p className="text-sm text-red-600">{errors.form}</p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons - Only show if no similarity results are displayed */}
        {!similarityResults && (
          <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-4 pt-4">
            <button
              type="submit"
              disabled={!isFormValid || isProcessing}
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 sm:py-4 sm:px-6 rounded-lg sm:rounded-xl hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed font-semibold cursor-pointer transition-all duration-200 active:scale-95 disabled:transform-none flex items-center justify-center text-sm sm:text-base"
            >
              {isCheckingSimilarity ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                  Checking for Similar Requests...
                </>
              ) : isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                  Submitting Your Idea...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  {user ? 'Submit Request' : 'Sign In to Submit'}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={isProcessing}
              className="px-4 py-3 sm:px-6 sm:py-4 text-gray-600 hover:text-gray-800 hover:bg-gray-100 font-semibold cursor-pointer rounded-lg sm:rounded-xl transition-colors text-sm sm:text-base disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Tips */}
        <div className="bg-blue-50 rounded-lg sm:rounded-xl p-4 mt-6">
          <h4 className="text-sm font-semibold text-blue-800 mb-2">💡 Tips for a great submission:</h4>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>• Start with a detailed description - our AI can suggest a great name!</li>
            <li>• Be specific about the problem your app solves</li>
            <li>• Explain who your target users are</li>
            <li>• Describe what makes your idea unique</li>
            <li>• Include why people would be willing to pay for it</li>
          </ul>
        </div>
      </form>
    </div>
  );
}